import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Icon from '../../../components/common/Icon';
import AnimatedCounter from '../../../components/common/AnimatedCounter';

import {
  getPublicClubsApi,
  getPublicMatchesApi,
} from '../../../api/match.api';

import { getPublicPostsApi } from '../../../api/content.api';
import { getPublicPlayersApi } from '../../../api/player.api';

const INITIAL_STATS = [
  {
    key: 'matches',
    icon: 'trophy',
    value: 0,
    label: 'مسابقه ثبت‌ شده',
    to: '/competitions',
    tone: 'emerald',
  },
  {
    key: 'players',
    icon: 'users',
    value: 0,
    label: 'بازیکن ثبت‌ شده',
    to: '/players',
    tone: 'cyan',
  },
  {
    key: 'clubs',
    icon: 'shield',
    value: 0,
    label: 'باشگاه عضو',
    to: '/clubs',
    tone: 'blue',
  },
  {
    key: 'posts',
    icon: 'news',
    value: 0,
    label: 'خبر و اطلاعیه',
    to: '/news',
    tone: 'amber',
  },
];

const TONE_CLASSES = {
  emerald: {
    icon:
      'border-emerald-400/20 bg-emerald-400/10 text-emerald-400 group-hover:bg-emerald-400 group-hover:text-[#07150F]',
    glow: 'bg-emerald-400/10',
  },

  cyan: {
    icon:
      'border-lime-400/20 bg-lime-400/10 text-lime-400 group-hover:bg-lime-400 group-hover:text-[#07150F]',
    glow: 'bg-lime-400/10',
  },

  blue: {
    icon:
      'border-blue-400/20 bg-blue-400/10 text-blue-400 group-hover:bg-blue-400 group-hover:text-[#07150F]',
    glow: 'bg-blue-400/10',
  },

  amber: {
    icon:
      'border-amber-400/20 bg-amber-400/10 text-amber-400 group-hover:bg-amber-400 group-hover:text-[#07150F]',
    glow: 'bg-amber-400/10',
  },
};

function normalizeCount(response) {
  const root = response?.data;
  const data = root?.data;
  const meta = root?.meta;

  const candidates = [
    meta?.total,
    meta?.count,
    data?.total,
    data?.count,
    data?.pagination?.total,
    data?.meta?.total,
    data?.meta?.count,
  ];

  const value = candidates.find(
    (item) =>
      item !== undefined &&
      item !== null &&
      !Number.isNaN(Number(item))
  );

  if (value !== undefined) {
    return Math.max(0, Number(value));
  }

  if (Array.isArray(data)) {
    return data.length;
  }

  if (Array.isArray(data?.items)) {
    return data.items.length;
  }

  if (Array.isArray(data?.matches)) {
    return data.matches.length;
  }

  if (Array.isArray(data?.clubs)) {
    return data.clubs.length;
  }

  if (Array.isArray(data?.posts)) {
    return data.posts.length;
  }

  if (Array.isArray(data?.players)) {
    return data.players.length;
  }

  return 0;
}

export default function Stats() {
  const [stats, setStats] = useState(INITIAL_STATS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadStats() {
      try {
        const results = await Promise.allSettled([
          getPublicMatchesApi({
            limit: 1,
            page: 1,
          }),

          getPublicPlayersApi({
            limit: 1,
            page: 1,
          }),

          getPublicClubsApi({
            limit: 1,
            page: 1,
          }),

          getPublicPostsApi({
            limit: 1,
            page: 1,
          }),
        ]);

        if (!mounted) return;

        const matches =
          results[0]?.status === 'fulfilled'
            ? normalizeCount(results[0].value)
            : 0;

        const players =
          results[1]?.status === 'fulfilled'
            ? normalizeCount(results[1].value)
            : 0;

        const clubs =
          results[2]?.status === 'fulfilled'
            ? normalizeCount(results[2].value)
            : 0;

        const posts =
          results[3]?.status === 'fulfilled'
            ? normalizeCount(results[3].value)
            : 0;

        setStats([
          {
            ...INITIAL_STATS[0],
            value: matches,
          },

          {
            ...INITIAL_STATS[1],
            value: players,
          },

          {
            ...INITIAL_STATS[2],
            value: clubs,
          },

          {
            ...INITIAL_STATS[3],
            value: posts,
          },
        ]);
      } catch {
        if (!mounted) return;
        setStats(INITIAL_STATS);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadStats();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="relative z-20 mx-auto -mt-16 max-w-[1280px] px-1 sm:-mt-20 sm:px-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        {stats.map((stat, index) => {
          const accent = index === 0;

          return (
            <Link
              key={stat.key}
              to={stat.to}
              className={[
                'group relative flex min-h-[148px] flex-col justify-between overflow-hidden rounded-[1.75rem] p-5 transition-all duration-500 hover:-translate-y-2',
                accent ? 'accent-card' : 'glass hover:shadow-card-hover',
              ].join(' ')}
            >
              {!accent && (
                <div className="pointer-events-none absolute -left-8 -top-8 h-28 w-28 rounded-full bg-emerald-400/15 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
              )}

              <div className="relative flex items-start justify-between">
                <div
                  className={[
                    'grid h-11 w-11 place-items-center rounded-2xl transition-all duration-500 group-hover:rotate-6 group-hover:scale-110',
                    accent
                      ? 'bg-ink text-floodlight'
                      : 'bg-ink/5 text-emerald-600 group-hover:bg-ink group-hover:text-floodlight',
                  ].join(' ')}
                >
                  <Icon name={stat.icon} className="h-5 w-5" />
                </div>

                <span
                  className={[
                    'grid h-8 w-8 place-items-center rounded-full transition-all duration-300 group-hover:-translate-x-1',
                    accent ? 'bg-ink/10 text-ink' : 'bg-slate-100 text-slate-400',
                  ].join(' ')}
                >
                  <Icon name="arrowLeft" className="h-4 w-4" />
                </span>
              </div>

              <div className="relative mt-4">
                <strong
                  className={[
                    'block text-3xl font-black tracking-tight sm:text-4xl',
                    accent ? 'text-ink' : 'text-ink',
                  ].join(' ')}
                >
                  {loading ? (
                    <span className="inline-block h-9 w-16 animate-pulse rounded-lg bg-ink/10" />
                  ) : (
                    <AnimatedCounter
                      value={stat.value}
                      duration={1800}
                      className="inline-block"
                    />
                  )}
                </strong>

                <span
                  className={[
                    'mt-1 block text-[11px] font-bold sm:text-xs',
                    accent ? 'text-ink/65' : 'text-slate-500',
                  ].join(' ')}
                >
                  {stat.label}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
